import { Component, Input, Output, ViewChild, EventEmitter } from '@angular/core';


import { ControlValueAccessorModel, getProvider } from '../../../../Common/Forms/models/control-value-accessor.model';
import { InputValidationModel } from '../../../../Common/Forms/models/input-validation.model';
import { TextInputComponent } from '../TextInput/text-input.component';
import { AgprComponentStyleEnum } from '../../../Basic/enums/social-analysis-types.enum';
import { ActionKeys } from '../../enums/keys.enum';

// if you need chips + a text input to add chips in a simple layout, this component should do that.
// doesn't do dropdown, autocomplete, autocomplete + dropdown etc.
/**
 * ### Accessibility Usage
 * + ***ariaDescribedBy*** - use this if the text input has an external error message, or it needs extra description for screen reader. Use message's id
 * + ***ariaInvalid*** - use this to indicate state if the component can fail in certain scenario
 * + ***ariaLabelledBy*** - use this to provide an accessible label for the text input, only when header is outside the component
 * + ***id*** - provide an unique id so that this component can be correctly recognized by screen readers. Use interpolation [id]="'unique_id'"
 */
@Component({
    selector: 'agpr-text-input-chips',
    styleUrls: ['./text-input-chips.component.scss'],
    templateUrl: './text-input-chips.component.html',
    providers: [getProvider(TextInputChipsComponent)],
    standalone: false
})
export class TextInputChipsComponent extends ControlValueAccessorModel<string[]> {
    readonly agprComponentStyle = AgprComponentStyleEnum;

    @Input() id: string;
    @Input() placeholder: string;
    @Input() disabled: boolean;
    @Input() header: string;
    @Input() tooltip: string;
    @Input() icon: string;
    @Input() iconTooltip: string;
    @Input() iconColor: string;
    @Input() splitOnPattern: RegExp | Function;
    @Input() trimQuotes: boolean;
    @Input() trimWhitespace: boolean = true;
    @Input() showDuplicateErrorKey: string = null;
    @Input() chipValidations: InputValidationModel[] = [];
    @Input() clearAllChipsTooltip: string = 'Core.button.clearAll';
    @Input() addOnKeys: ActionKeys[] = [ ActionKeys.TAB, ActionKeys.ENTER ];
    @Input() style: AgprComponentStyleEnum = AgprComponentStyleEnum.social;
    @Input() ariaDescribedBy: string = '';
    @Input() ariaInvalid: boolean;
    @Input() ariaLabel: string = '';
    @Input() ariaLabelledBy: string = '';
    @Input() readonly: boolean = false;
    @Input() useLink: boolean = false;
    @Input() alwaysShowChips: boolean = false; // If true, always show the chips container, even if there are no chips.
    @Input() chipPlaceholder?: string;
    @Input() required: boolean = false;

    @Output() blur: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();

    @ViewChild('textInput', { static: true }) textInput: TextInputComponent;

    value: string[] = [];
    newChip: string;
    chipValidationErrors: string[] = [];


    writeValue(value: string[]): void {
        this.value = value ?? [];
    }

    addChip(chip: string): void {
        if (chip.length) {
            if (this.value?.find(chip_ => chip_ === chip)) {
                if (this.showDuplicateErrorKey) {
                    if (!this.chipValidationErrors.find(error => error === this.showDuplicateErrorKey)) {
                        this.chipValidationErrors.push(this.showDuplicateErrorKey);
                    }
                    this.newChip = '';
                    this.textInput.clearInput();
                }
                return;
            }

            if (!this.value) {
                this.value = [];
            }

            this.value.push(chip);
        }
        this.newChip = '';
        this.textInput.clearInput();
        this.onModelChange(this.value);
    }

    processChip(chip: string): string {
        if (this.trimQuotes) {
            chip = chip?.replace(/"/g, '');
        }
        if (this.trimWhitespace) {
            chip = chip?.trim();
        }
        return chip;
    }

    chipIsValid(chip: string): boolean {
        let valid = true;
        for (let i = 0; i < this.chipValidations.length; i++) {
            if (!this.chipValidations[i].isValid(chip)) {
                valid = false;

                // don't add duplicate errors
                if (!this.chipValidationErrors.find(error => error === this.chipValidations[i].errorKey)) {
                    this.chipValidationErrors.push(this.chipValidations[i].errorKey);
                }
            }
        }
        return valid;
    }

    tryWrite() {
        this.chipValidationErrors = [];
        if (!this.newChip || this.newChip.length === 0) {
            return;
        }
        if (!!this.splitOnPattern) {
            let newChips: string[] = [];
            if (typeof this.splitOnPattern === 'function') {
                newChips = this.splitOnPattern(this.newChip);
            } else {
                newChips = this.newChip.split(this.splitOnPattern as RegExp);
            }

            for (let i = 0; i < newChips.length; i++) {
                const patternChip: string = this.processChip(newChips[i]);
                if (!!patternChip && this.chipIsValid(patternChip)) {
                    this.addChip(patternChip);
                }
            }
            return;
        }

        const newChip: string = this.processChip(this.newChip);
        if (this.chipIsValid(newChip)) {
            this.addChip(newChip);
        }
    }

    onKeyDown(event: KeyboardEvent): void {
        if (!this.addOnKeys || !this.addOnKeys.find(key => key === event.key)) {
            return;
        }
        if (event.key === ActionKeys.TAB && (!this.newChip || this.newChip.length === 0)) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        this.tryWrite();
    }

    focusInput() {
        requestAnimationFrame(() => this.textInput.input.nativeElement.focus());
    }

    onBlur(event: FocusEvent) {
        this.tryWrite();
        this.onModelTouched();
        this.blur.emit(event);
    }
}
